/**
 * Arbre des points de vente (sources de données) et sélection en cascade,
 * partagés par le sélecteur du header et le filtre « Points de vente » des
 * pages de statistiques.
 */

/** Point de vente tel que le renvoient `fetchDataSources` et sa variante. */
export interface DataSourceOption {
  label: string;
  shortLabel?: string;
  value: number;
  parentId: number | null;
  /** Absent pour les listes de sources actives seulement. */
  active?: boolean;
}

export interface DataSourceTreeItem {
  label: string;
  shortLabel: string;
  value: number;
  parentId: number | null;
  isChild: boolean;
  hasChildren: boolean;
  active: boolean;
}

export interface DataSourceTree {
  /** Toutes les sources reçues. */
  items: DataSourceTreeItem[];
  /** Ordre d'affichage : sources seules, puis chaque parent suivi de ses enfants. */
  flat: DataSourceTreeItem[];
  /** Enfants de chaque parent. */
  childrenMap: Map<number, number[]>;
}

export function buildDataSourceTree(sources: DataSourceOption[]): DataSourceTree {
  const childrenMap = new Map<number, number[]>();

  sources.forEach((source) => {
    if (source.parentId) {
      if (!childrenMap.has(source.parentId)) childrenMap.set(source.parentId, []);
      (childrenMap.get(source.parentId) as number[]).push(source.value);
    }
  });

  const items: DataSourceTreeItem[] = sources.map((source) => ({
    label: source.label,
    shortLabel: source.shortLabel || source.label,
    value: source.value,
    parentId: source.parentId || null,
    isChild: !!source.parentId,
    hasChildren: childrenMap.has(source.value),
    active: source.active !== false,
  }));

  const parentIds = new Set(childrenMap.keys());
  const standalones = items.filter((item) => !item.parentId && !parentIds.has(item.value));
  const groups = items
    .filter((item) => !item.parentId && parentIds.has(item.value))
    .map((parent) => [parent, ...items.filter((child) => child.parentId === parent.value)]);

  return {
    items,
    flat: [...standalones, ...groups.flat()],
    childrenMap,
  };
}

/**
 * Sélection après un clic : cocher un parent coche ses enfants, le décocher les
 * décoche, et un parent est coché exactement quand tous ses enfants le sont.
 * Sans changement induit, la sélection reçue est rendue telle quelle.
 */
export function syncDataSourceSelection(
  previous: number[],
  next: number[],
  childrenMap: Map<number, number[]>,
): number[] {
  const current = new Set(next);
  const before = new Set(previous);
  let changed = false;

  const added = next.filter((id) => !before.has(id));
  const removed = previous.filter((id) => !current.has(id));

  added.forEach((id) => {
    (childrenMap.get(id) ?? []).forEach((childId) => {
      if (!current.has(childId)) {
        current.add(childId);
        changed = true;
      }
    });
  });

  removed.forEach((id) => {
    (childrenMap.get(id) ?? []).forEach((childId) => {
      if (current.has(childId)) {
        current.delete(childId);
        changed = true;
      }
    });
  });

  childrenMap.forEach((children, parentId) => {
    const allSelected = children.every((childId) => current.has(childId));
    if (allSelected && !current.has(parentId)) {
      current.add(parentId);
      changed = true;
    }
    if (!allSelected && current.has(parentId)) {
      current.delete(parentId);
      changed = true;
    }
  });

  return changed ? Array.from(current) : next;
}
